<template>
  <!-- Same alert as the conflict check announcement, so an announcement looks like one. -->
  <div v-if="visible" class="print:hidden">
    <v-alert
      type="info"
      variant="tonal"
      density="compact"
      closable
      class="mb-4 mt-4 text-sm"
      data-cy="matters-upgrade-notice"
      @click:close="dismiss"
    >
      <template #text>
        The {{ matterTerm }}s page has been rebuilt to support bulk editing.
        <a
          :href="SUPPORT_URL"
          target="_blank"
          rel="noopener noreferrer"
          class="underline"
          data-cy="matters-upgrade-link"
          >See what changed</a
        >
      </template>
    </v-alert>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useMeStore } from '@/services/UserService/useMeStore';
import { getSupportArticleUrl } from '@/services/supportUrlService';

const SUPPORT_URL = getSupportArticleUrl('mattersPageUpgrade');

// Remembered on the account rather than in localStorage, so dismissing it on one
// machine dismisses it everywhere. Versioned so a later announcement starts fresh
// rather than inheriting this dismissal.
const NOTICE_KEY = 'mattersPageUpgrade.v1';

const meStore = useMeStore();
const { me, settings } = storeToRefs(meStore);

const matterTerm = computed(() => me.value?.terms?.Matter || 'Matter');

// Hidden immediately on close, before the PUT lands - and left hidden if it fails, so
// the worst a failed save can do is bring the notice back on the next visit.
const closed = ref(false);
const dismissedNotices = computed(() => settings.value?.dismissedNotices ?? []);
const visible = computed(
  () => !closed.value && !dismissedNotices.value.includes(NOTICE_KEY),
);

// ll-standards-ignore LL040 -- updateMe() catches its own errors and never rethrows
// (useMeStore.ts), so a try/catch here would be a branch that cannot run. The rule guards
// against an unhandled rejection stranding a spinner; there is no spinner and no rejection.
async function dismiss(): Promise<void> {
  closed.value = true;
  // The endpoint takes the whole array, not an append, so send the keys already dismissed
  // alongside the new one.
  await meStore.updateMe({
    settings: { dismissedNotices: [...dismissedNotices.value, NOTICE_KEY] },
  });
}
</script>
